import * as React from 'react';
import px from 'prop-types';
import { useTranslation } from 'Common/hooks';
import {
    HINTS,
    HINT_FALLBACKS,
    HINT_RESOURCE_KEYS,
    WRAPPER_FALLBACKS,
    WRAPPER_RESOURCE_KEYS,
    formatLinkHint,
    getLinkHints,
} from 'Common/utils/LinkAccessibility';

/**
 * Visually hidden text describing how a link behaves, for example " (PDF, opens in a new window)".
 *
 * Renders nothing when the link needs no explanation, so it is safe to drop into every anchor
 * unconditionally. The server-side equivalent is Html.LinkHint in
 * BGrav.Epi.CMS.Extensions.HtmlHelperLinkAccessibilityExtensions.
 *
 * Where the hint has to live in an attribute rather than the DOM (an aria-label on an icon-only
 * link, say), use the useLinkHintText hook below instead.
 */
export default function LinkHint({ href, target }) {
    const text = useLinkHintText(href, target);

    if (!text) return null;

    return <span className="visually-hidden">{text}</span>;
}

LinkHint.propTypes = {
    href: px.string,
    target: px.string,
};

/**
 * Resolves the hint text for a link, translated where a resource exists and falling back to English
 * otherwise.
 *
 * Every translation lookup runs on every render regardless of which hints apply -- useTranslation is
 * a hook, so the calls cannot be made conditional on the classification result.
 *
 * @param {string} href The link destination.
 * @param {string} [target] The anchor target attribute.
 * @returns {string} The hint text including its leading space, or an empty string.
 */
export function useLinkHintText(href, target) {
    const newWindow = useTranslation(HINT_RESOURCE_KEYS[HINTS.NEW_WINDOW], null, {
        notFoundValue: HINT_FALLBACKS[HINTS.NEW_WINDOW],
    });

    const pdf = useTranslation(HINT_RESOURCE_KEYS[HINTS.PDF], null, {
        notFoundValue: HINT_FALLBACKS[HINTS.PDF],
    });

    const download = useTranslation(HINT_RESOURCE_KEYS[HINTS.DOWNLOAD], null, {
        notFoundValue: HINT_FALLBACKS[HINTS.DOWNLOAD],
    });

    const email = useTranslation(HINT_RESOURCE_KEYS[HINTS.EMAIL], null, {
        notFoundValue: HINT_FALLBACKS[HINTS.EMAIL],
    });

    const phone = useTranslation(HINT_RESOURCE_KEYS[HINTS.PHONE], null, {
        notFoundValue: HINT_FALLBACKS[HINTS.PHONE],
    });

    const prefix = useTranslation(WRAPPER_RESOURCE_KEYS.prefix, null, {
        notFoundValue: WRAPPER_FALLBACKS.prefix,
    });

    const suffix = useTranslation(WRAPPER_RESOURCE_KEYS.suffix, null, {
        notFoundValue: WRAPPER_FALLBACKS.suffix,
    });

    const separator = useTranslation(WRAPPER_RESOURCE_KEYS.separator, null, {
        notFoundValue: WRAPPER_FALLBACKS.separator,
    });

    const hints = React.useMemo(() => getLinkHints(href, target), [href, target]);

    const translations = React.useMemo(
        () => ({
            [HINTS.NEW_WINDOW]: newWindow,
            [HINTS.PDF]: pdf,
            [HINTS.DOWNLOAD]: download,
            [HINTS.EMAIL]: email,
            [HINTS.PHONE]: phone,
        }),
        [newWindow, pdf, download, email, phone]
    );

    const wrapper = React.useMemo(() => ({ prefix, suffix, separator }), [prefix, suffix, separator]);

    return React.useMemo(() => formatLinkHint(hints, translations, wrapper), [hints, translations, wrapper]);
}

/**
 * Appends the hint to an author supplied accessible name, so an icon-only link keeps its label and
 * still announces its behaviour.
 *
 * @param {string} label The accessible name the caller would otherwise have used.
 * @param {string} href The link destination.
 * @param {string} [target] The anchor target attribute.
 * @returns {string} The label with the hint appended.
 */
export function useLinkLabel(label, href, target) {
    const hint = useLinkHintText(href, target);

    return React.useMemo(() => {
        if (!hint) return label || '';

        return label ? `${label}${hint}` : hint.trimStart();
    }, [label, hint]);
}
